import { getHostname } from '../api'
import { PriceType } from '../types/api'
import { type Elemental, getRouteFromElemental } from '../types/elemental'

const getAllProductJsonldImages = (images: Record<string, any>) => {
  const allImages = []
  for (const key in images) {
    if (typeof images[key] === 'string') {
      allImages.push(images[key])
      continue
    }
    if (images[key]?.length)
      allImages.push(...images[key].map((image: any) => image.url))
  }
  return allImages.map((a) => {
    return {
      '@type': 'ImageObject',
      'contentUrl': a,
    }
  })
}

interface GetProductJsonLdParams {
  elemental: Elemental
  elementalUrl: string
  featuredImage: string
  description: string
  category: string
}

export const getProductJsonLdFromElemental = (params: GetProductJsonLdParams) => {
  const { elemental, elementalUrl, description, category, featuredImage } = params
  const host = getHostname()
  const elementalTypeSlug = getRouteFromElemental(elementalUrl.replace(host, ''))

  const obj = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `elemental-${elementalTypeSlug}-${elemental?.id}`,
    'name': elemental?.title,
    'image': getAllProductJsonldImages([
      ...Object.values(elemental?.images || {}),
      featuredImage,
    ]),
    'description': description,
    'brand': {
      '@type': 'Brand',
      'name': elemental?.user?.name,
      'url': `${host}/user/${elemental?.user?.username}`,
    },
    'productID': elemental?.id,
    'releaseDate': new Date(elemental?.createdAt).toISOString().split('T').shift(),
    'url': elementalUrl,
    'category': category,
    'keywords': (elemental?.topics || []).map(a => a.name),
    'offers': {
      '@type': 'Offer',
      'description': elemental.priceTypeId !== PriceType.fairPrice ? 'One-time purchase' : 'Fair-price',
      'availability': 'https://schema.org/InStock',
      'url': elementalUrl,
      'priceSpecification': {
        '@type': 'PriceSpecification',
        'priceCurrency': 'USD',
        'price': formatNumber((elemental.price || 0) / 100),
      },
      'seller': {
        '@context': 'https:\/\/schema.org',
        '@type': 'Person',
        'description': '',
        'url': `${host}/user/${elemental?.user?.username}`,
        'name': elemental?.user?.name,
        'image': elemental?.user?.avatar,
      },
      'hasMerchantReturnPolicy': {
        '@type': 'MerchantReturnPolicy',
        'returnPolicyCategory': 'https://schema.org/NoReturn',
      },
      'itemDelivery': {
        '@type': 'DigitalDeliveryMethod',
        'name': 'Digital delivery',
      },
      'shippingDetails': {
        '@type': 'OfferShippingDetails',
        'shippingRate': {
          '@type': 'MonetaryAmount',
          'value': '0',
          'currency': 'USD',
        },
        'description': 'This product is delivered electronically via email or download link immediately after purchase.',
      },
    },

  }

  if (elemental.priceOriginal && elemental.price && elemental.priceOriginal > elemental.price) {
    obj.offers.priceSpecification.wasPrice = formatNumber(elemental.priceOriginal / 100)
    obj.offers.priceSpecification.discount = elemental.discountPercentual || 0
    obj.offers.priceSpecification.discountCurrency = 'USD'
    obj.offers.priceSpecification.validFrom = new Date(elemental?.updatedAt).toISOString().split('T').shift()
  }

  return obj
}

export const getBreadcrumbsJsonLd = (breadcrumbs: { name: string; url: string }[]) => {
  const host = getHostname()

  const result = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        name: 'Home',
        url: `${host}/`,
      },
      ...breadcrumbs,
    ].map((item, index) => {
      return {
        '@type': 'ListItem',
        'position': index + 1,
        'name': item.name,
        'item': item.url.startsWith('http') ? item.url : `${host}${item.url}`,
      }
    }),
  }
  return result
}
